import useOptimisticDeleteProposalGroup from '@/api/mutations/proposals/optimistic/useOptimisticDeleteProposalGroup';
import useOptimisticUpdateProposalGroup from '@/api/mutations/proposals/optimistic/useOptimisticUpdateProposalGroup';
import useCreateProposalGroup from '@/api/mutations/proposals/useCreateProposalGroup';
import ConfirmDeleteModal from '@/ui/components/confirm-delete-modal/ConfirmDeleteModal';
import DragHandleWithDropdown from '@/ui/components/dragHandleWithDropdown/DragHandleWithDropdown';
import { groupHeadingId } from '@/ui/pages/proposalBuilderPage/util';
import {
  AnchorButton,
  Badge,
  Button,
  Field,
  Form,
  TDropdownItem,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

export type TProps = {
  proposalId: string;
  id: string;
  title?: string | null;
  depth: number;
  addItemTo: string;
  selectionMode?: 'MULTI_CHOICE' | 'OPTIONAL' | 'REQUIRED';
  /** Whether the group is collapsed (its children are hidden). */
  collapsed?: boolean;
  /** Toggles the group's collapsed state; absent for groups without children. */
  onToggleCollapse?: () => void;
  hasMultiChoiceSubGroup: boolean;
  hasOptionalSubGroup: boolean;
};

type FormValues = {
  title: string;
};

/**
 * Doubles as the untitled group's heading and the edit input's placeholder, so
 * the title reads the same whether or not it is being edited.
 */
const GROUP_TITLE_PLACEHOLDER = 'Add your group title here';

const LineItemGroupRow: FC<TProps> = (props) => {
  const {
    proposalId,
    id,
    title,
    addItemTo,
    depth,
    selectionMode = 'REQUIRED',
    collapsed = false,
    onToggleCollapse,
    hasMultiChoiceSubGroup,
    hasOptionalSubGroup,
  } = props;

  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const { mutate: createProposalGroup } = useCreateProposalGroup({
    proposalId,
  });
  const { mutate: updateProposalGroup } = useOptimisticUpdateProposalGroup({
    proposalId,
  });
  const { mutate: deleteProposalGroup } = useOptimisticDeleteProposalGroup({
    proposalId,
  });

  const formMethods = useForm<FormValues>({
    values: {
      title: title ?? '',
    },
  });

  const titleField = formMethods.register('title');

  // Enter and blur are both commit paths, and Escape unmounts the input — which
  // can itself fire a blur. This makes the first of them the only one that runs.
  const isEditFinishedRef = useRef(false);

  const commitTitle = (values: FormValues) => {
    if (isEditFinishedRef.current) return;
    isEditFinishedRef.current = true;

    // A blank title is stored as null so the group stays untitled and keeps
    // showing the placeholder.
    const trimmedTitle = values.title.trim();
    updateProposalGroup({
      proposalId,
      groupKey: id,
      title: trimmedTitle === '' ? null : values.title,
    });

    setIsEditing(false);
  };

  const cancelEditing = () => {
    isEditFinishedRef.current = true;
    setIsEditing(false);
    formMethods.reset();
  };

  const startEditing = () => {
    isEditFinishedRef.current = false;
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditing) {
      formMethods.setFocus('title', { shouldSelect: true });
    }
  }, [isEditing, formMethods]);

  const isSubGroup = depth > 0;

  const contextMenuOptions: TDropdownItem[] = [
    {
      label: 'Delete',
      onClick: () => setIsConfirmingDelete(true),
    },
  ];

  if (depth === 0) {
    contextMenuOptions.unshift(
      {
        label: 'Add multi-select group',
        disabled: hasMultiChoiceSubGroup,
        disabledReasons: ['Cannot have multiple multi-select groups'],
        onClick: () => {
          createProposalGroup({
            proposalId,
            parent: {
              groupKey: id,
            },
            source: { new: { selectionMode: 'MULTI_CHOICE' } },
          });
        },
      },
      {
        label: 'Add optional group',
        disabled: hasOptionalSubGroup,
        disabledReasons: ['Cannot have multiple optional groups'],
        onClick: () => {
          createProposalGroup({
            proposalId,
            parent: {
              groupKey: id,
            },
            source: { new: { selectionMode: 'OPTIONAL' } },
          });
        },
      }
    );
  }

  return (
    <>
      <div
        className={clstx(
          'flex gap-2.5 p-2.5 items-center w-full relative rounded-lg',
          (depth === 0 || isEditing) && 'bg-gray-50'
        )}
      >
        <DragHandleWithDropdown
          className={clstx(isSubGroup && '-m-2')}
          id={id}
          label="group"
          items={contextMenuOptions}
        />

        {onToggleCollapse && (
          <Button
            className="aspect-square"
            size="xs"
            variant="quaternary"
            aria-label={collapsed ? 'Expand group' : 'Collapse group'}
            onClick={onToggleCollapse}
          >
            <UntitledIcon
              name="chevron-down"
              className={clstx(
                'size-4 transition-transform',
                collapsed && '-rotate-90'
              )}
            />
          </Button>
        )}

        {isEditing && (
          <Form
            className="flex gap-2.5 items-center flex-1 relative"
            methods={formMethods}
            onSubmit={commitTitle}
          >
            <Field name="title" className="flex-1 min-w-0">
              <input
                className="bg-transparent focus:outline-none w-full p-0 font-medium placeholder:font-normal placeholder:text-gray-600"
                placeholder={GROUP_TITLE_PLACEHOLDER}
                // Field's label wires `htmlFor` to an id `register` never
                // puts on the input, so the name comes from here. The
                // placeholder is a call to action, not a label.
                aria-label="Group title"
                {...titleField}
                onBlur={(event) => {
                  void titleField.onBlur(event);
                  void formMethods.handleSubmit(commitTitle)();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') cancelEditing();
                }}
              />
            </Field>
          </Form>
        )}

        {!isEditing && (
          <>
            <div
              className={clstx(
                'flex gap-2.5 justify-between items-center flex-1 min-w-0 relative',
                isSubGroup &&
                  'before:h-px before:bg-gray-300 before:w-full before:absolute before:left-0'
              )}
            >
              {isSubGroup ? (
                <span
                  id={groupHeadingId(id)}
                  role="heading"
                  aria-level={5}
                  className="sr-only"
                >
                  {title || 'No title'}
                </span>
              ) : (
                <div
                  id={groupHeadingId(id)}
                  role="heading"
                  aria-level={4}
                  className="grow min-w-0"
                >
                  <Button
                    className={clstx(
                      'w-full justify-start',
                      title ? 'font-medium' : 'font-normal'
                    )}
                    size="xs"
                    variant="quaternary"
                    onClick={startEditing}
                  >
                    <span
                      className={clstx('truncate', !title && 'text-gray-600')}
                    >
                      {title || GROUP_TITLE_PLACEHOLDER}
                    </span>
                  </Button>
                </div>
              )}
            </div>

            {selectionMode === 'MULTI_CHOICE' && (
              <Badge size="sm" variant="soft">
                Multi-select
              </Badge>
            )}

            {selectionMode === 'OPTIONAL' && (
              <Badge size="sm" variant="soft">
                Optional
              </Badge>
            )}

            <AnchorButton
              as={Link}
              to={addItemTo}
              size="xs"
              variant="secondary"
            >
              Add item
            </AnchorButton>
          </>
        )}
      </div>
      <ConfirmDeleteModal
        isOpen={isConfirmingDelete}
        onClose={() => setIsConfirmingDelete(false)}
        onConfirmDelete={() => {
          // The delete is optimistic, so the modal closes immediately rather
          // than waiting on the server.
          deleteProposalGroup({ proposalId, groupKey: id });
          setIsConfirmingDelete(false);
        }}
        entityName={{ singular: 'group', plural: 'groups' }}
      />
    </>
  );
};

export default LineItemGroupRow;
